import { useState } from 'preact/hooks';
import dayjs from 'dayjs';
import { rpx } from 'client/lib/rpx-client';
import { useAsyncState } from 'client/lib/hooks';
import { Spinner } from '@components/spinner';
import { IcoRefresh } from '@components/icons';
import { HeadingSecondary } from '@course/components/headings';
import { fmtFullTime } from 'shared/dateutil';

type Props = {
  courseId: UUID;
  meetingId: UUID;
};

type Response = 'accepted' | 'declined' | 'tentative' | 'awaiting';

type RosterRow = {
  sendId: UUID;
  recipientUserId: UUID;
  recipientEmail: string;
  recipientName?: string;
  inviteType: 'host' | 'member';
  sentAt: Date;
  response?: Response;
  respondedAt?: Date;
  forwarded: boolean;
  responseEmail?: string;
};

export function RsvpSection({ courseId, meetingId }: Props) {
  const [data, setData] = useAsyncState(
    () => rpx.meetings.getMeetingRsvpStatus({ courseId, meetingId }),
    [courseId, meetingId],
  );
  const [showHosts, setShowHosts] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  async function refresh() {
    setRefreshing(true);
    try {
      const fresh = await rpx.meetings.getMeetingRsvpStatus({ courseId, meetingId });
      setData(fresh);
    } finally {
      setRefreshing(false);
    }
  }

  if (!data) {
    return (
      <div class="bg-white rounded-lg border border-gray-200 p-6 flex justify-center">
        <Spinner class="border-sky-500 w-6 h-6" />
      </div>
    );
  }

  const { memberCounts, hostCounts, roster } = data;

  if (memberCounts.invited === 0 && hostCounts.invited === 0) {
    return (
      <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-2">
        <HeadingSecondary>RSVPs</HeadingSecondary>
        <p class="opacity-75 text-sm">
          No invites have been sent for this meeting yet. Once they go out (see the banner above),
          member responses will appear here.
        </p>
      </div>
    );
  }

  const memberRoster = roster.filter((r) => r.inviteType === 'member');
  const hostRoster = roster.filter((r) => r.inviteType === 'host');

  return (
    <div class="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-5">
      <div class="flex items-start gap-3">
        <div class="flex flex-col gap-1 grow">
          <HeadingSecondary>RSVPs</HeadingSecondary>
          <p class="text-sm opacity-75">
            Member responses to the calendar invite. Hosts and replies from a different address are
            noted inline.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing}
          class="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline disabled:opacity-50 disabled:no-underline"
          title="Refresh RSVPs"
        >
          <IcoRefresh class={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <CountsRow counts={memberCounts} label="Members" />

      {hostCounts.invited > 0 && (
        <div class="flex flex-col gap-3">
          <button
            type="button"
            class="text-sm text-sky-700 hover:underline self-start"
            onClick={() => setShowHosts((v) => !v)}
          >
            {showHosts ? 'Hide host RSVPs' : `Show host RSVPs (${hostCounts.invited})`}
          </button>
          {showHosts && <CountsRow counts={hostCounts} label="Hosts" />}
        </div>
      )}

      {memberRoster.length > 0 && <RosterList title="Members" rows={memberRoster} />}
      {showHosts && hostRoster.length > 0 && <RosterList title="Hosts" rows={hostRoster} />}
    </div>
  );
}

function CountsRow({
  counts,
  label,
}: {
  counts: {
    accepted: number;
    declined: number;
    tentative: number;
    awaiting: number;
    invited: number;
  };
  label: string;
}) {
  return (
    <div class="flex flex-wrap items-center gap-2 text-sm">
      <span class="text-xs uppercase tracking-wide opacity-60 mr-1">{label}</span>
      {counts.accepted > 0 && <Pill tone="ok" count={counts.accepted} label="Yes" />}
      {counts.tentative > 0 && <Pill tone="warn" count={counts.tentative} label="Maybe" />}
      {counts.declined > 0 && <Pill tone="err" count={counts.declined} label="No" />}
      <Pill tone="neutral" count={counts.awaiting} label="Awaiting" />
      <span class="opacity-60 ml-1">of {counts.invited} invited</span>
    </div>
  );
}

type PillTone = 'ok' | 'warn' | 'err' | 'neutral';

function Pill({ tone, count, label }: { tone: PillTone; count: number; label: string }) {
  const toneClasses =
    tone === 'ok'
      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
      : tone === 'warn'
        ? 'bg-amber-50 text-amber-900 border-amber-200'
        : tone === 'err'
          ? 'bg-rose-50 text-rose-800 border-rose-200'
          : 'bg-gray-50 text-gray-700 border-gray-200';
  return (
    <span
      class={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-0.5 ${toneClasses}`}
    >
      <strong class="tabular-nums">{count}</strong>
      <span>{label}</span>
    </span>
  );
}

function RosterList({ title, rows }: { title: string; rows: RosterRow[] }) {
  return (
    <div class="flex flex-col gap-2">
      <h4 class="text-xs uppercase tracking-wide opacity-60">{title}</h4>
      <ul class="flex flex-col divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
        {rows.map((row) => (
          <RosterRowItem key={row.sendId} row={row} />
        ))}
      </ul>
    </div>
  );
}

function RosterRowItem({ row }: { row: RosterRow }) {
  const display = row.recipientName || row.recipientEmail;
  const response: Response = row.response ?? 'awaiting';
  return (
    <li class="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
      <span class="flex flex-col leading-tight min-w-0 grow">
        <strong class="truncate">{display}</strong>
        <span class="opacity-70 truncate">{row.recipientEmail}</span>
      </span>
      <ResponseBadge response={response} />
      <span class="opacity-60 text-xs whitespace-nowrap">
        {row.respondedAt ? dayjs(row.respondedAt).fromNow() : `sent ${fmtFullTime(row.sentAt)}`}
      </span>
      {row.forwarded && row.responseEmail && (
        <span
          class="text-xs px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900 whitespace-nowrap"
          title={`Reply came from ${row.responseEmail} — invite was sent to ${row.recipientEmail}`}
        >
          replied from {row.responseEmail}
        </span>
      )}
    </li>
  );
}

function ResponseBadge({ response }: { response: Response }) {
  if (response === 'accepted') {
    return (
      <span class="inline-flex items-center gap-1 text-emerald-700 font-medium text-xs">✓ Yes</span>
    );
  }
  if (response === 'tentative') {
    return (
      <span class="inline-flex items-center gap-1 text-amber-700 font-medium text-xs">~ Maybe</span>
    );
  }
  if (response === 'declined') {
    return (
      <span class="inline-flex items-center gap-1 text-rose-700 font-medium text-xs">✗ No</span>
    );
  }
  return <span class="inline-flex items-center gap-1 opacity-60 text-xs">Awaiting</span>;
}
